import {Entidades} from '../clientes/entidades';
import {AbstractControl, FormArray, FormControl, FormGroup, Validators} from '@angular/forms';
import {Paises} from '../aplicacao/paises';
import {DAY} from 'app/views/artigos/renovavel-form/renovavel-form.component';
import {DataTypes} from 'app/views/gestao-arquivos/ga-tipo-campo/ga-tipo-campo.component';
import {Permissoes} from '../aplicacao/permissoes';
import * as XLSX from 'xlsx';

export class Util {

    public OpcaoNaoUsado = 1;

    static timeInput(target: HTMLInputElement) {
        const position = target.selectionStart;
        target.value = this.timeFormat(target.value);
        const offset = Number(target.value.length === 4);
        target.selectionStart = position + offset;
        target.selectionEnd = target.selectionStart;
    }

    static timeFormat(value: string) {
        let formattedValue = value.replace(/\D/g, '');

        if (formattedValue.length > 2) {
            const minutes = parseInt(formattedValue.slice(2), 10) > 59 ? 59 : formattedValue.slice(2);
            const hours = parseInt(formattedValue.slice(0, 2), 10) > 23 ? 23 : formattedValue.slice(0, 2);
            formattedValue = `${hours}:${minutes}`;
        }

        return formattedValue.slice(0, 5);
    }

    static formatTimeWithTwoDigits(date: Date): string {
        if (!date) {
            return '00:00';
        }
        date = new Date(date);
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        date.setSeconds(0);
        return `${hours}:${minutes}`;
    }

    static timeStringToDecimal(timeString: string): number {
        const [hoursStr, minutesStr] = timeString.split(':');
        const hours = parseInt(hoursStr, 10);
        const minutes = parseInt(minutesStr, 10);
        return this.timeDecimal(hours, minutes);
    }

    static groupByCodeArea(permissoes: Permissoes[]): Record<string, Permissoes[]> {
        return permissoes.reduce((groups, permissao) => {
            const {area} = permissao;
            if (!groups[area]) {
                groups[area] = [];
            }
            groups[area].push(permissao);
            return groups;
        }, {} as Record<string, Permissoes[]>);
    }

    static organizarTiposArtigo(tipos: any[]): any[] {
        return tipos.reduce((acc, element) => {
            const updatedAcc = [...acc, element];
            if (element.tiposFilhos?.length > 0) {
                return [...updatedAcc, ...this.organizarTiposArtigo(element.tiposFilhos)];
            }
            return updatedAcc;
        }, []);
    }

    static repeat(arr: any, n: number) {
        return [].concat(...Array(n).fill(arr));
    }

    static timeDecimal(hours: number, minutes: number) {
        const decimalHours = hours + (minutes / 60);
        return Math.round(decimalHours * 100) / 100;
    }

    static base64ToArray(base64: string) {
        const bytes = this.base64ToArrayBuffer(base64);
        const workbook = XLSX.read(bytes, {type: 'array'});
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        return XLSX.utils.sheet_to_json(worksheet);
    }

    static sliceArrayIntoChunks(dataArray: any, chunkSize: number, transform?: (item: any) => any) {
        const chunks = [];
        for (let i = 0; i < dataArray.length; i += chunkSize) {
            const chunk = dataArray.slice(i, i + chunkSize);
            chunks.push(transform ? transform(chunk) : chunk);
        }
        return chunks;
    }

    static bytesToBase64(bytes: string) {
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
            binary += String.fromCharCode(bytes.charCodeAt(i));
        }
        return btoa(binary);
    }

    static arrayToBase64(arrayChunk) {
        const jsonString = JSON.stringify(arrayChunk);
        const bytes = new TextEncoder().encode(jsonString);
        const binaryString = String.fromCharCode(...bytes);
        return btoa(binaryString);
    }

    static deepCompare(obj1, obj2) {
        // Check if the two items are the same object in memory
        if (obj1 === obj2) {
            return true;
        }

        // Check if either item is null or not an object
        if (typeof obj1 !== 'object' || obj1 === null ||
            typeof obj2 !== 'object' || obj2 === null) {
            return obj1 === obj2;
        }

        // Check if both items are arrays
        if (Array.isArray(obj1) && Array.isArray(obj2)) {
            if (obj1.length !== obj2.length) {
                return false;
            }
            for (let i = 0; i < obj1.length; i++) {
                if (!this.deepCompare(obj1[i], obj2[i])) {
                    return false;
                }
            }
            return true;
        }

        // Check if both items are dates
        if (obj1 instanceof Date && obj2 instanceof Date) {
            return obj1.getTime() === obj2.getTime();
        }

        // Check if both items are regular expressions
        if (obj1 instanceof RegExp && obj2 instanceof RegExp) {
            return obj1.toString() === obj2.toString();
        }

        // Get the keys of both objects
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        // Check if the objects have the same number of properties
        if (keys1.length !== keys2.length) {
            return false;
        }

        // Check if all properties in obj1 exist in obj2 with the same values
        for (const key of keys1) {
            if (!keys2.includes(key) || !this.deepCompare(obj1[key], obj2[key])) {
                return false;
            }
        }

        return true;
    }

    static areObjectsEqual(obj1, obj2) {
        // Get the keys of the objects
        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        // Check if the number of properties is the same
        if (keys1.length !== keys2.length) {
            return false;
        }

        // Check the values of each property
        for (const key of keys1) {
            // Check if the property exists in both objects
            if (!(key in obj2)) {
                return false;
            }

            // Compare the values of the properties
            if (obj1[key] !== obj2[key]) {
                return false;
            }
        }

        // All properties have the same values
        return true;
    }

    static getValue(obj: any, key: string) {
        const keys = key.split('.');
        let value = obj;

        for (const element of keys) {
            value = value ? value[element] : null;
        }

        return value;
    }

    static compareObjectsByKeys(obj1: any, obj2: any, keys: string[]) {
        // Check if both inputs are objects
        if (typeof obj1 !== 'object' || typeof obj2 !== 'object') {
            return false;
        }

        // Compare each key
        return keys.every(key => this.getValue(obj1, key) === this.getValue(obj2, key));
    }

    static timeString(decimalTime) {
        const hours = Math.floor(decimalTime);
        const minutes = Math.round((decimalTime % 1) * 60);
        return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }

    static formatTime(date: Date) {
        date = date ? new Date(date) : null;
        if (isNaN(date?.getTime())) {
            return '';
        }
        const d0 = new Date(date);
        const m0 = d0.getMinutes() < 10 ? `0${d0.getMinutes()}` : `${d0.getMinutes()}`;
        const h0 = d0.getHours() < 10 ? `0${d0.getHours()}` : `${d0.getHours()}`;
        return `${h0}:${m0}`;
    }

    static moradaInput(e: Event) {
        const textarea = e.target as HTMLTextAreaElement;
        const lineHeight = parseInt(getComputedStyle(textarea).lineHeight, 10);
        const lines = Math.floor(textarea.scrollHeight / lineHeight);
        const brs = textarea.value.split('\n');

        if (brs.length > 3) {
            textarea.value = brs.slice(0, 3).join('\n');
            return;
        }

        if (lines > 3) {
            textarea.value = textarea.value.slice(0, -1);
        }
    }

    static duration(startDate: Date, endDate: Date) {
        const t0 = startDate?.getTime();
        const tf = endDate?.getTime();
        if ((isNaN(t0) || isNaN(tf)) || (startDate > endDate)) {
            return '';
        }

        const duration = tf - t0;
        const totalSeconds = Math.floor(duration / 1000);

        if (totalSeconds < 60) {
            return `${totalSeconds} segundos`;
        } else if (totalSeconds < 3600) {
            const totalMinutes = Math.floor(totalSeconds / 60);
            return `${totalMinutes} minutos`;
        } else if (totalSeconds < 86400) {
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            return `${hours} horas, ${minutes} minutos`;
        } else if (totalSeconds < 2592000) {
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor(((totalSeconds % 86400) % 3600) / 60);
            return `${days} dias, ${hours} horas, ${minutes} minutos`;
        } else if (totalSeconds < 31536000) {
            const months = Math.floor(totalSeconds / 2592000);
            return `${months} meses`;
        } else {
            const years = Math.floor(totalSeconds / 31536000);
            return `${years} anos`;
        }
    }

    static calculateMinutes(startDate: Date, endDate: Date) {
        const t0 = startDate?.getTime();
        const tf = endDate?.getTime();
        if ((isNaN(t0) || isNaN(tf)) || (startDate > endDate)) {
            return null;
        }
        const duration = tf - t0;
        const diffInMinutes = duration / (1000 * 60);
        return Math.round(diffInMinutes);
    }

    static calculateIntervals(startDate: Date, endDate: Date, intervaloMinutos: number) {
        const t0 = startDate?.getTime();
        const tf = endDate?.getTime();
        if ((isNaN(t0) || isNaN(tf)) || (startDate > endDate)) {
            return null;
        }
        const duration = tf - t0;
        const intervalDurationInMilliseconds = intervaloMinutos * 1000 * 60;
        return duration / intervalDurationInMilliseconds;
    }

    static parseDuration(durationString: string): number[] {
        const parts = durationString.split(', ');
        const result: number[] = [0, 0, 0, 0, 0];

        for (const part of parts) {
            if (part.endsWith(' anos')) {
                result[0] = parseInt(part.replace(' anos', ''), 10);
            } else if (part.endsWith(' meses')) {
                result[1] = parseInt(part.replace(' meses', ''), 10);
            } else if (part.endsWith(' dias')) {
                result[2] = parseInt(part.replace(' dias', ''), 10);
            } else if (part.endsWith(' horas')) {
                result[3] = parseInt(part.replace(' horas', ''), 10);
            } else if (part.endsWith(' minutos')) {
                result[4] = parseInt(part.replace(' minutos', ''), 10);
            }
        }

        return result;
    }

    static base64ToArrayBuffer(base64: string): ArrayBuffer {
        const byteString = atob(base64.split(',')[1]);
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
        }
        return ab;
    }

    static UTCDate(inicio: Date, fim: Date) {
        if (inicio && fim) {
            const d0 = new Date(inicio);
            const d1 = new Date(fim);
            return [
                new Date(Date.UTC(d0.getFullYear(), d0.getMonth(), d0.getDate())),
                new Date(Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate()))
            ];
        }
        return [];
    }

    static _UTCDate(date: Date | string) {
        date = date ? new Date(date) : null;
        return date ? new Date(Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            date.getHours(),
            date.getMinutes())) : null;
    }

    static clamp(value: number, min: number, max: number) {
        return Math.min(Math.max(value, min), max);
    }

    static addDays(date: Date, qtd: number = 1): Date {
        return new Date(date.getTime() + qtd * DAY);
    }

    static isLeapYear(year: number): boolean {
        return ((year % 4 === 0) && (year % 100 !== 0)) || (year % 400 === 0);
    }

    static getDaysInMonth(year: number, month: number): number {
        return [31, (this.isLeapYear(year) ? 29 : 28), 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
    }

    static addMonths(date: Date, value: number = 1): Date {
        const n = date.getDate();
        const df = new Date(date);
        df.setDate(1);
        df.setMonth(df.getMonth() + value);
        df.setDate(Math.min(n, this.getDaysInMonth(df.getFullYear(), df.getMonth())));
        return df;
    }

    static getControlErrors(control: FormControl, name: string, errorMap: { [p: string]: string }) {
        let errorMessage = '';
        if (control.errors) {
            Object.keys(control.errors).forEach((errorKey: string) => {
                switch (errorKey) {
                    case 'min':
                        errorMessage += `${name}: ${errorMap[errorKey]}. Min: ${control.errors['min']['min']}. \n`;
                        break;
                    case 'max':
                        errorMessage += `${name}: ${errorMap[errorKey]}. Max: ${control.errors['max']['max']}. \n`;
                        break;
                    case 'lesserThan':
                        errorMessage += `${name}: ${errorMap[errorKey]}. Max: ${control.errors['lesserThan']['min']}. \n`;
                        break;
                    case 'greaterThan':
                        errorMessage += `${name}: ${errorMap[errorKey]}. Min: ${control.errors['greaterThan']['max']}. \n`;
                        break;
                    case 'lesserThanEqual':
                        errorMessage += `${name}: ${errorMap[errorKey]}. Max: ${control.errors['lesserThanEqual']['min']}. \n`;
                        break;
                    case 'greaterThanEqual':
                        errorMessage += `${name}: ${errorMap[errorKey]}. Min: ${control.errors['greaterThanEqual']['max']}. \n`;
                        break;
                    default:
                        errorMessage += `${name}: ${errorMap[errorKey]}. \n`;
                        break;
                }
            });
        }

        return errorMessage;
    }

    static getFormErrors(form: FormGroup | FormArray, formNameMap: { [key: string]: string }, errorMap: { [key: string]: string }): string {
        let errorMessage = '';

        // Iterate over all the controls in the form group
        Object.keys(form.controls).forEach((key: string) => {
            const control = form.controls[key];

            // If the control is a form group, recursively call the function
            if (control instanceof FormGroup || control instanceof FormArray) {
                const nestedErrorMessage = Util.getFormErrors(control, formNameMap, errorMap);

                if (nestedErrorMessage) {
                    errorMessage += `${formNameMap[key] ?? `[${parseInt(key, 10) + 1}]`}: ${nestedErrorMessage}\n`;
                }
            }

            // If the control has errors, concatenate the error messages
            if (control.errors) {
                Object.keys(control.errors).forEach((errorKey: string) => {
                    switch (errorKey) {
                        case 'min':
                            errorMessage += `${formNameMap[key]}: ${errorMap[errorKey]}. Min: ${control.errors['min']['min']}. \n`;
                            break;
                        case 'max':
                            errorMessage += `${formNameMap[key]}: ${errorMap[errorKey]}. Max: ${control.errors['max']['max']}. \n`;
                            break;
                        case 'lesserThan':
                            errorMessage += `${formNameMap[key]}: ${errorMap[errorKey]}. Max: ${control.errors['lesserThan']['min']}. \n`;
                            break;
                        case 'greaterThan':
                            errorMessage += `${formNameMap[key]}: ${errorMap[errorKey]}. Min: ${control.errors['greaterThan']['max']}. \n`;
                            break;
                        case 'lesserThanEqual':
                            errorMessage += `${formNameMap[key]}: ${errorMap[errorKey]}. Max: ${control.errors['lesserThanEqual']['min']}. \n`;
                            break;
                        case 'greaterThanEqual':
                            errorMessage += `${formNameMap[key]}: ${errorMap[errorKey]}. Min: ${control.errors['greaterThanEqual']['max']}. \n`;
                            break;
                        case 'minlength':
                            errorMessage += `${formNameMap[key] ?? `[${parseInt(key, 10) + 1}]`}: Deve ter no minímo ${control.errors['minlength']['requiredLength']} caracteres. Comprimento: ${control.errors['minlength']['actualLength']}. \n`;
                            break;
                        case 'maxlength':
                            errorMessage += `${formNameMap[key] ?? `[${parseInt(key, 10) + 1}]`}: Deve ter no máximo ${control.errors['maxlength']['requiredLength']} caracteres. Comprimento: ${control.errors['maxlength']['actualLength']}. \n`;
                            break;
                        case 'invalid':
                            errorMessage += `${formNameMap[key] ?? `[${parseInt(key, 10) + 1}]`}: ${errorMap[errorKey]}.\n E-mails: ${control.errors['invalid']['emails']}. \n`;
                            break;
                        default:
                            errorMessage += `${formNameMap[key] ?? `[${parseInt(key, 10) + 1}]`}: ${errorMap[errorKey]}. \n`;
                            break;
                    }
                });
            }
        });

        return errorMessage;
    }

    static getErrorKey(form: FormGroup): string {
        for (const key of Object.keys(form.controls)) {
            const control = form.controls[key];

            // If the control is a form group, recursively call the function
            if (control instanceof FormGroup) {
                const errorKey = Util.getErrorKey(control);
                if (errorKey) {
                    return errorKey;
                }
            }

            // If the control has errors return the key
            if (control.errors) {
                const parentKey = this.findParentKey(control);
                return parentKey ? `${parentKey}.${key}` : key;
            }
        }

        return '';
    }

    static parseValue(value: string, type: string) {
        switch (type) {
            case DataTypes.BOOL:
                value = String(value)?.toLowerCase()?.trim();
                return value === 'true' || value === 'sim';
            case DataTypes.DATE:
                return value ? new Date(value) : '';
            default:
                return value;
        }
    }

    static stringfyValue(value: string, type: string) {
        switch (type) {
            case DataTypes.BOOL:
                return value === 'true' ? 'Sim' : 'Não';
            case DataTypes.DATE:
                return this.formatDate(new Date(value));
            default:
                return value;
        }
    }

    static formatDate(date) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }

    static collectTree(list: any[], filtered: any[], key: string, fk: string) {
        let result = [...filtered];
        for (const child of filtered) {
            result = [...result, ...this.collectTree(list, list.filter(a => child[key] === a[fk]), key, fk)];
        }
        return result;
    }

    static compareLists(list1: any[], list2: any[]) {
        const added = [];
        const removed = [];
        const modified = [];

        // Check for added elements
        for (const item of list2) {
            if (!list1.some((x) => x.id === item.id)) {
                added.push(item);
            }
        }

        // Check for removed elements
        for (const item of list1) {
            if (!list2.some((x) => x.id === item.id)) {
                removed.push(item);
            }
        }

        // Check for modified elements
        for (const item2 of list2) {
            const item1 = list1.find((x) => x.id === item2.id);
            if (item1 && JSON.stringify(item1) !== JSON.stringify(item2)) {
                modified.push({old: item1, new: item2});
            }
        }

        return {added, removed, modified};
    }

    static findParentKey(formControl: AbstractControl): string | null {
        const root = formControl.parent?.parent;
        if (!root) {
            return null;
        }

        return Object.keys(root.controls).find(key => root.controls[key] === formControl.parent) || null;
    }

    static getLabelProvincia(codPais: string, paises?: Paises[]): string {
        return paises?.find((p) => p.codPais === codPais)?.idOpcCodigoPostal < 2
            ? 'Distrito'
            : 'Província';
    }

    static getLabelMunicipio(codPais: string, paises?: Paises[]): string {
        return paises?.find((p) => p.codPais === codPais)?.idOpcCodigoPostal < 2
            ? 'Concelho'
            : 'Munícipio';
    }

    public static isHttpRequest(url: string): boolean {
        const regex = /^(http:\/\/|https:\/\/)/;
        return regex.test(url);
    }

    public static filterNonOverlappingDates(objects: any[]) {
        return objects.filter((obj, index, arr) => {
            const {dataInicio, dataFim} = obj;

            // Check if there is any overlap with previous objects
            for (let i = 0; i < index; i++) {
                const prevObj = arr[i];
                if (dataInicio <= prevObj.dataFim && dataFim >= prevObj.dataInicio) {
                    return false; // Overlapping dates, exclude the object
                }
            }

            return true; // Non-overlapping dates, include the object
        });
    }

    public static strToDate(valor: string) {
        if (valor) {
            const dataString = valor
                .split('-')
                .reverse()
                .toString()
                .replace(',', '-')
                .replace(',', '-');
            const dataConvertida = new Date(dataString).getTime();
            return dataConvertida;
        }

        return '';
    }

    public static strParaDataISO(dataStr: any) {
        if (dataStr) {
            return Util.FromStrToDate(
                new Intl.DateTimeFormat('pt-PT').format(Date.parse(dataStr))
            );
        } else {
            return '';
        }
    }

    public static FromStrToDate(valor: any) {
        return valor
            ? valor
                .split('/')
                .reverse()
                .toString()
                .replace(',', '-')
                .replace(',', '-')
            : '';
    }

    public static convertToFloat(valor: string | number): number {
        if (typeof (valor) === 'number') {
            return this.DuasCasasDecimais(valor);
        }

        if (valor?.includes(',')) {
            return this.DuasCasasDecimais(parseFloat(
                String(valor)
                    .replaceAll('.', '')
                    .replace(',', '.')
                    .replace(/[^0-9.,]/g, ''))
            );
        } else {
            return this.DuasCasasDecimais(parseFloat(
                String(valor)
                    .replace(',', '.')
                    .replace(/[^0-9.,]/g, ''))
            );
        }
    }

    static round(value: number, decimals: number = 0): number {
        if (decimals < 0) {
            throw new Error('Decimals must be non-negative');
        }

        const factor = Math.pow(10, decimals);
        const tempValue = value * factor;
        const roundedTemp = Math.round(tempValue);
        let diff = Math.abs(tempValue - roundedTemp);
        diff = Math.round(diff * factor) / factor;

        if (diff >= 0.5) {
            return Math.round(tempValue + Math.sign(tempValue) * 0.001) / factor;
        }

        return Number((Math.round(tempValue) / factor).toFixed(decimals));
    }

    public static convertToFloat3Casas(valor: string | number): number {
        if (typeof (valor) === 'number') {
            return this.TresCasasDecimais(valor);
        }
        return this.TresCasasDecimais(parseFloat(
            String(valor)
                .replaceAll('.', '')
                .replace(',', '.')
                .replace(/[^0-9.,]/g, ''))
        );
    }

    static TresCasasDecimais(valor?: number | string): number {
        if (typeof valor === 'string') {
            // Tenta converter a string em um número.
            valor = valor.replace('.', '');
            valor = parseFloat(valor.replace(',', '.'));
        }

        if (typeof valor !== 'number' || isNaN(valor)) {
            // Se o valor não é um número válido, retorne zero ou outro valor padrão.
            return 0;
        }

        return Math.round((valor || 0) * 1000) / 1000;
    }

    static DuasCasasDecimais(valor?: number | string): number {
        if (typeof valor === 'string') {
            if (valor?.includes(',')) {
                valor = valor.replace('.', '');
            }
            valor = parseFloat(valor.replace(',', '.'));
        }

        if (typeof valor !== 'number' || isNaN(valor)) {
            return 0;
        }

        return Math.round((valor || 0) * 100) / 100;
    }


    public static replaceSliceInStrToDate(valor: any) {
        if (valor) {
            return valor.split('/').toString().replace(',', '-').replace(',', '-');
        }
        return '';
    }

    public static getData(dia: number, mes: number, ano: any): string {
        const mesActual = mes + 1;
        const data =
            ano.toString() +
            '-' +
            (mesActual < 10 ? '0' + mesActual.toString() : mesActual.toString()) +
            '-' +
            (dia < 10 ? '0' + dia.toString() : dia.toString());
        return data;
    }

    public static momToDate(momentDate: any): string {
        if (momentDate) {
            if (momentDate._isAMomentObject) {
                const mesActual = momentDate._i.month + 1;
                const data =
                    momentDate._i.year.toString() +
                    '-' +
                    (mesActual < 10 ? '0' + mesActual.toString() : mesActual.toString()) +
                    '-' +
                    (momentDate._i.date < 10
                        ? '0' + momentDate._i.date.toString()
                        : momentDate._i.date.toString());
                return data;
            } else {
                const rgx = new RegExp('/', 'gi');
                const date = Intl.DateTimeFormat('en-GB').format(new Date(momentDate));
                return date.replace(rgx, '-');
            }
        } else {
            return;
        }
    }

    public static getAnoData(dia: number, mes: number, ano: any): string {
        return ano;
    }

    public static getLabelNome(entidade: Entidades) {
        return entidade.idOpcPriUltimoNome === 1 ? 'Nome' : 'Nome completo';
    }

    public static alterarFormatoData(data: string) {
        if (data) {
            return (
                data.substring(6, 11) +
                '-' +
                data.substring(3, 5) +
                '-' +
                data.substring(0, 2)
            );
        }
        return '';
    }

    public static getFormErros(form: AbstractControl) {
        if (form instanceof FormControl) {
            return form.errors ?? null;
        }
        if (form instanceof FormArray) {
            return form.errors ?? null;
        }
        if (form instanceof FormGroup) {
            const groupErrors = form.errors;
            const formErrors = groupErrors ? {groupErrors} : {};
            Object.keys(form.controls).forEach((key) => {
                const error = this.getFormErros(form.get(key));
                if (error !== null) {
                    formErrors[key] = error;
                }
            });
            return Object.keys(formErrors).length > 0 ? formErrors : null;
        }
    }

    public static formatarDecimal(strNum: any): string {
        const valor = strNum.toString();
        if (valor) {
            if (valor.includes('.')) {
                const valorComVirgula = valor.toString().replace('.', ',');
                const partesDoNumero = valorComVirgula.split(',');
                let numeroFormatado = '';
                if (partesDoNumero[1].length === 1) {
                    numeroFormatado = partesDoNumero[0] + ',' + partesDoNumero[1] + '0';
                } else {
                    numeroFormatado = partesDoNumero[0] + ',' + partesDoNumero[1];
                }
                return numeroFormatado;
            } else {
                return valor + ',00';
            }
        } else {
            return '0,00';
        }
    }

    public static reverterDecimal(valor: any) {
        if (valor && typeof valor === 'string' && valor.includes(',')) {
            const valorComPonto = valor.toString().replace(',', '.');
            return parseFloat(valorComPonto);
        } else {
            return valor;
        }
    }

    public static fromStrToDate(date: any) {
        const dtTime = new Intl.DateTimeFormat('pt-PT', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric'
        }).format(Date.parse(date));
        return dtTime;
    }

    public static DownloadXML(dados) {
        const element = document.createElement('a');
        const blob = new Blob([dados.objecto], {
            type: 'text/xml'
        });
        element.href = URL.createObjectURL(blob);
        element.setAttribute('download', dados.mensagem);
        document.body.appendChild(element);
        element.click();
    }

    public static downloadAnexo(anexo: string, nomeAnexo: string) {
        if (!anexo) {
            return;
        }
        const link = document.createElement('a');
        link.href = anexo;
        link.download = nomeAnexo;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    public static midpointRound(number) {
        return number < 0 ? -Math.round(-number * 2) / 2 : Math.round(number * 2) / 2;
    }

    public static CurrencyEnToPt(valor) {
        const delimiter = ',';
        const val = valor.split('.', 2);
        const d = val[1];
        let i = parseInt(val[0], 10);
        if (isNaN(i)) {
            return '';
        }
        let minus = '';
        if (i < 0) {
            minus = '-';
        }
        i = Math.abs(i);
        let n = String(i);
        const valTemp = [];
        while (n.length > 3) {
            const nn = n.substr(n.length - 3);
            valTemp.unshift(nn);
            n = n.substr(0, n.length - 3);
        }
        if (n.length > 0) {
            val.unshift(n);
        }
        n = valTemp.join(delimiter);
        if (d.length < 1) {
            valor = n;
        } else {
            valor = n + '.' + d;
        }
        valor = minus + valor;
        return valor;
    }

    public static validarCodigoLivre(classificacao, form: any, campo: any) {
        if (!classificacao) {
            const valorCodigo = form.get(campo).value;
            if (valorCodigo.trim().length < 1) {
                form.get(campo).setValue('');
                form.get(campo).setValidators(Validators.required);
                form.get(campo).updateValueAndValidity();
                form.get(campo).markAsTouched({onlySelf: true});
                return false;
            }
        }
    }

    // Função para formatação de casas decimais //

    public static formatarCasasDecimais(valor) {
        // Valor string //
        if (valor && typeof valor === 'string') {
            if (valor.includes(',')) {
                const decimalPart = valor.split(',', 2)[1];
                const integerPart = valor.split(',', 2)[0];
                if (decimalPart.length > 1) {
                    return valor;
                } else {
                    return integerPart + ',' + decimalPart + '0';
                }
            } else {
                return valor + ',00';
            }
        }

        // Valor numérico //
        if (valor && typeof valor === 'number') {
            const num = '' + valor;
            if (num.includes('.')) {
                const decimalPart = num.split('.', 2)[1];
                const integerPart = num.split('.', 2)[0];
                if (decimalPart.length > 1) {
                    return valor;
                } else {
                    return integerPart + ',' + decimalPart + '0';
                }
            } else {
                return valor + ',00';
            }
        }
    }

    public static DataConvertida(data: any): string {
        if (data) {
            const d = new Date(data);
            const novaData = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(
                d.getDate()
            )}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            return novaData;
        } else {
            return null;
        }
    }

    public static AnoData(data: any): number {
        if (data) {
            const d = new Date(data);
            const novaData = d.getFullYear();
            return novaData;
        } else {
            return null;
        }
    }

    static crawl(a: any, prop: string) {
        const keyParts = prop.split('.');
        let value = a;
        for (const part of keyParts) {
            value = value[part];
        }
        return value;
    }

    static parseDate(value: Date | string) {
        if (!value) {
            return null;
        }
        if (typeof value === 'string') {
            const [datePart, timePart] = value.split('T');
            const [yy, MM, dd] = datePart.split('-').map(Number);
            const [hh, mm] = timePart.split(':').map(Number);
            return new Date(yy, MM - 1, dd, hh, mm);
        }
        return new Date(value);
    }

    strParaDouble(valor: any) {
        if (valor) {
            return valor.replaceAll(/\./gi, '').replace(',', '.');
        } else {
            return 0;
        }
    }
}


function pad(value) {
    return value.toString().padStart(2, 0);
}
